import WizardPageWrapper, { GeneralPageSummary } from "../WizardPageWrapper";
import {
  Col,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
} from "antd";
import { CoverWizardForm, FlowType, StateCode } from "../../CoverWizardModal";
import Flex from "../../../../../../components/Flex";
import { useEffect, useMemo, useRef, useState } from "react";
import CustomerInfoDto from "../../../../../../store/actions/dtos/CustomerInfoDto";
import { CustomerAddressDto } from "../../../../../../store/actions/dtos/CustomerAddressDto";
import DynamicLoadingSkeleton from "../../../../../../components/DynamicLoadingSkeleton";
import zipCodes from "zipcodes";
import FormItemLabel from "antd/lib/form/FormItemLabel";

export type AutofilledValue = {
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingStateCode: string;
  shippingZip: string;
};

type ShippingInformationPageProps = {
  onNextButtonClick?: () => void;
  onBackButtonClick?: () => void;
  usStateOptions: Array<StateCode>;
  getCustomerInfo: () => Promise<CustomerInfoDto | undefined>;
  getCustomerAddresses: () => Promise<CustomerAddressDto[] | undefined>;
  shippingInformationPageState: ShippingInformationPageState;
};

export enum AutofillRadio {
  poolOwner = "poolOwner",
  billTo = "billTo",
  manual = "manual",
  shipTo = "shipTo",
}

type SelectOption = {
  label: string;
  value: string;
};

const { Option } = Select;

export default function ShippingInformationPage({
  onNextButtonClick,
  onBackButtonClick,
  usStateOptions,
  getCustomerInfo,
  getCustomerAddresses,
  shippingInformationPageState,
}: ShippingInformationPageProps) {
  const {
    currentlyAutofilledValueState,
    selectedRadioState,
    shipToAddressesState,
    selectedShipToItemState,
  } = shippingInformationPageState;
  const [currentlyAutofilledValue, setCurrentlyAutofilledValue] =
    currentlyAutofilledValueState;
  const [selectedRadio, setSelectedRadio] = selectedRadioState;
  const [shipToAddresses, setShipToAddresses] = shipToAddressesState;
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedShipToItem, setSelectedShipToItem] = selectedShipToItemState;

  const form = Form.useFormInstance<CoverWizardForm>();

  function autoFillPoolOwnerInfo() {
    const formValues = form.getFieldsValue(true);
    const newValues = {
      shippingName: !!formValues.jobName ? formValues.jobName : "",
      shippingAddress: !!formValues.address ? formValues.address : "",
      shippingCity: !!formValues.city ? formValues.city : "",
      shippingZip: !!formValues.zip ? formValues.zip : "",
      shippingStateCode: !!formValues.stateCode ? formValues.stateCode : "",
    };
    form.setFieldsValue(newValues);
    setSelectedRadio(AutofillRadio.poolOwner);
    setCurrentlyAutofilledValue(newValues);
  }

  async function autoFillBillToInfo() {
    const response = await getCustomerInfo();
    if (
      !!response &&
      (!!response.contact ||
        !!response.address ||
        !!response.cityName ||
        !!response.zipCode ||
        !!response.stateId)
    ) {
      const autofilledValue = {
        shippingName: response.company,
        shippingAddress: !!response.address ? response.address.trim() : "",
        shippingCity: !!response.cityName ? response.cityName.trim() : "",
        shippingZip: !!response.zipCode ? response.zipCode.trim() : "",
        shippingStateCode: !!response.stateId ? response.stateId.trim() : "",
      };
      form.setFieldsValue(autofilledValue);
      setSelectedRadio(AutofillRadio.billTo);
      setCurrentlyAutofilledValue(autofilledValue);
    }
  }

  const name =
    Form.useWatch<string>("shippingName", {
      preserve: true,
      form: form,
    }) ?? null;
  const address =
    Form.useWatch<string>("shippingAddress", {
      preserve: true,
      form: form,
    }) ?? null;
  const city =
    Form.useWatch<string>("shippingCity", {
      preserve: true,
      form: form,
    }) ?? null;
  const stateCode =
    Form.useWatch<string>("shippingStateCode", {
      preserve: true,
      form: form,
    }) ?? null;
  const zip =
    Form.useWatch<string>("shippingZip", {
      preserve: true,
      form: form,
    }) ?? null;

  useEffect(() => {
    //necessary as the watch values sometimes don't update all at once
    const values = form.getFieldsValue(true) as CoverWizardForm;

    if (currentlyAutofilledValue !== null) {
      const valueIsCustom =
        values.shippingName !== currentlyAutofilledValue.shippingName ||
        values.shippingAddress !== currentlyAutofilledValue.shippingAddress ||
        values.shippingCity !== currentlyAutofilledValue.shippingCity ||
        values.shippingZip !== currentlyAutofilledValue.shippingZip ||
        values.shippingStateCode !== currentlyAutofilledValue.shippingStateCode;

      if (valueIsCustom && selectedRadio !== AutofillRadio.manual) {
        setSelectedRadio(AutofillRadio.manual);
        setCurrentlyAutofilledValue(null);
      }
    }
  }, [
    name,
    address,
    city,
    stateCode,
    zip,
    currentlyAutofilledValue,
    selectedRadio,
  ]);

  function autoFill(option: AutofilledValue) {
    form.setFieldsValue({
      shippingName: !!option.shippingName ? option.shippingName : "",
      shippingAddress: !!option.shippingAddress ? option.shippingAddress : "",
      shippingCity: !!option.shippingCity ? option.shippingCity : "",
      shippingStateCode: !!option.shippingStateCode
        ? option.shippingStateCode
        : "",
      shippingZip: !!option.shippingZip ? option.shippingZip : "",
    });
  }

  async function setInitialAutofillValues() {
    const {
      shippingLastName,
      shippingAddress,
      shippingCity,
      shippingStateCode,
      shippingZip,
    } = form.getFieldsValue(true) || {};
    setLoading(true);
    const addresses = await getCustomerAddresses();
    setLoading(false);
    if (!!addresses) {
      setShipToAddresses(addresses);

      // set default values if no values are present
      if (
        !shippingLastName &&
        !shippingAddress &&
        !shippingCity &&
        !shippingStateCode &&
        !shippingZip
      ) {
        const defaultShipToAddress = addresses.find(
          (address) => address.isDefault
        );

        if (!!defaultShipToAddress) {
          const newValue = {
            shippingName: defaultShipToAddress.company.trim(),
            shippingAddress: defaultShipToAddress.address1.trim(),
            shippingCity: defaultShipToAddress.city.trim(),
            shippingZip: defaultShipToAddress.zip.trim(),
            shippingStateCode: defaultShipToAddress.state.trim(),
          };
          setCurrentlyAutofilledValue(newValue);
          setSelectedRadio(AutofillRadio.shipTo);
          autoFill(newValue);
          setSelectedShipToItem({
            value: newValue.shippingAddress,
            label: newValue.shippingName,
          });
        } else {
          autoFillBillToInfo();
        }
      }
    }

    return [];
  }

  useEffect(() => {
    setInitialAutofillValues();
  }, []);

  return (
    <WizardPageWrapper
      title="Shipping Instructions"
      summary={<GeneralPageSummary />}
      onBackButtonClick={onBackButtonClick}
      onNextButtonClick={onNextButtonClick}
      disableNext={!name || !address || !city || !stateCode || !zip || loading}
    >
      <Flex vertical gap="1rem" style={{ marginTop: "0.5rem" }}>
        <Radio.Group
          value={selectedRadio}
          onChange={(e) => setSelectedRadio(e.target.value)}
        >
          <Flex vertical gap="1rem">
            <Flex
              style={{ width: "100%" }}
              justify="space-between"
              align="center"
            >
              <DynamicLoadingSkeleton loading={loading}>
                <Radio
                  value={AutofillRadio.poolOwner}
                  onClick={autoFillPoolOwnerInfo}
                >
                  Pool Owner Information
                </Radio>
              </DynamicLoadingSkeleton>
              <DynamicLoadingSkeleton loading={loading}>
                <Radio
                  value={AutofillRadio.billTo}
                  onClick={autoFillBillToInfo}
                  style={{ width: "165px" }}
                >
                  Bill-To Information
                </Radio>
              </DynamicLoadingSkeleton>
            </Flex>
            <Flex
              style={{ width: "100%" }}
              justify="space-between"
              align="center"
            >
              <DynamicLoadingSkeleton loading={loading}>
                <Radio value={AutofillRadio.manual}>Fill out manually</Radio>
              </DynamicLoadingSkeleton>
              <DynamicLoadingSkeleton loading={loading}>
                <ShipToAutofillRadio
                  setShipToInfo={autoFill}
                  setSelectedAutofillItem={setSelectedRadio}
                  autofillShipToInfo={autoFill}
                  customerAddresses={shipToAddresses}
                  setRadioValue={setSelectedRadio}
                  setCurrentlyAutofilledValue={setCurrentlyAutofilledValue}
                  selectedShipToItemState={[
                    selectedShipToItem,
                    setSelectedShipToItem,
                  ]}
                />
              </DynamicLoadingSkeleton>
            </Flex>
          </Flex>
        </Radio.Group>
        <Row gutter={[10, 10]}>
          <Col span={24}>
            <DynamicLoadingSkeleton
              loading={loading}
              width="100%"
              height="42px"
              style={{ marginTop: "26px" }}
            >
              <Form.Item<CoverWizardForm>
                style={{ margin: "0" }}
                name="shippingName"
                label="Name"
                required
              >
                <Input placeholder="John Doe" />
              </Form.Item>
            </DynamicLoadingSkeleton>
          </Col>
          <Col span={24}>
            <DynamicLoadingSkeleton
              loading={loading}
              width="100%"
              height="42px"
              style={{ marginTop: "26px" }}
            >
              <Form.Item<CoverWizardForm>
                style={{ margin: "0" }}
                name="shippingAddress"
                label="Address"
                required
              >
                <Input placeholder="1600 Pennsylvania Avenue" />
              </Form.Item>
            </DynamicLoadingSkeleton>
          </Col>
          <Col xs={12} md={5}>
            <DynamicLoadingSkeleton
              loading={loading}
              width="100%"
              height="42px"
              style={{ marginTop: "26px" }}
            >
              <ZipCodeInputWithAutofill
                value={zip}
                form={form}
                city={city}
                state={stateCode}
                stateFieldName="shippingStateCode"
                cityFieldName="shippingCity"
                zipFieldName="shippingZip"
              />
            </DynamicLoadingSkeleton>
          </Col>
          <Col xs={24} md={14}>
            <DynamicLoadingSkeleton
              loading={loading}
              width="100%"
              height="42px"
              style={{ marginTop: "26px" }}
            >
              <Form.Item<CoverWizardForm>
                style={{ margin: "0" }}
                name="shippingCity"
                label="City"
                required
              >
                <Input placeholder="Washington" />
              </Form.Item>
            </DynamicLoadingSkeleton>
          </Col>
          <Col xs={12} md={5}>
            <DynamicLoadingSkeleton
              loading={loading}
              width="100%"
              height="42px"
              style={{ marginTop: "26px" }}
            >
              <Form.Item<CoverWizardForm>
                style={{ margin: "0" }}
                name="shippingStateCode"
                label="State"
                required
              >
                <Select placeholder="AZ">
                  {usStateOptions.map((state: any) => (
                    <Option key={state.id} value={state.id}>
                      {state.id}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </DynamicLoadingSkeleton>
          </Col>
        </Row>
      </Flex>
    </WizardPageWrapper>
  );
}

function ShipToAutofillRadio({
  setRadioValue,
  setCurrentlyAutofilledValue,
  autofillShipToInfo,
  setSelectedAutofillItem,
  customerAddresses,
  selectedAutofillItem,
  selectedShipToItemState: [selectedShipToItem, setSelectedShipToItem],
}: {
  setRadioValue: (value: AutofillRadio) => void;
  setCurrentlyAutofilledValue: (value: AutofilledValue) => void;
  autofillShipToInfo: (value: AutofilledValue) => void;
  customerAddresses: CustomerAddressDto[] | null;
  selectedAutofillItem?: string;
  setShipToInfo: (value: AutofilledValue) => void;
  setSelectedAutofillItem: (value: AutofillRadio) => void;
  selectedShipToItemState: [
    SelectOption | null,
    React.Dispatch<React.SetStateAction<SelectOption | null>>
  ];
}) {
  return (
    <Flex align="center">
      <Radio
        disabled={!customerAddresses?.length}
        value={AutofillRadio.shipTo}
        onClick={() => {
          let selectedFullAddress: CustomerAddressDto | undefined;
          if (!!selectedShipToItem && !!customerAddresses?.length) {
            selectedFullAddress = customerAddresses.find(
              (address) =>
                address.address1.toLowerCase().trim() ===
                selectedShipToItem.value.toLowerCase().trim()
            );
          }
          if (!!selectedFullAddress) {
            const newValue = {
              shippingName: selectedFullAddress.company,
              shippingAddress: selectedFullAddress.address1,
              shippingCity: selectedFullAddress.city,
              shippingZip: selectedFullAddress.zip,
              shippingStateCode: selectedFullAddress.state,
            };
            autofillShipToInfo(newValue);
            setCurrentlyAutofilledValue(newValue);
            setRadioValue(AutofillRadio.shipTo);
          }
        }}
      ></Radio>
      <Select
        placeholder="Ship to address"
        dropdownMatchSelectWidth={false}
        style={{ width: "150px" }}
        value={selectedShipToItem}
        disabled={!customerAddresses?.length}
        labelInValue
        options={
          customerAddresses?.map((address) => ({
            label: address.company,
            value: address.address1,
          })) ?? []
        }
        onChange={(value) => {
          setSelectedAutofillItem(AutofillRadio.shipTo);
          const selectedAddress = customerAddresses?.find(
            (address) => address.address1 === value.value
          );
          if (!!selectedAddress) {
            const newValue = {
              shippingName: selectedAddress.company,
              shippingAddress: selectedAddress.address1,
              shippingCity: selectedAddress.city,
              shippingZip: selectedAddress.zip,
              shippingStateCode: selectedAddress.state,
            };
            setCurrentlyAutofilledValue(newValue);
            autofillShipToInfo(newValue);
            setSelectedShipToItem(value);
          }
        }}
        onBlur={() => {
          if (!selectedAutofillItem) {
            setRadioValue(AutofillRadio.manual);
          }
        }}
      />
    </Flex>
  );
}

export function ZipCodeInputWithAutofill({
  value,
  form,
  city,
  state,
  stateFieldName,
  cityFieldName,
  zipFieldName,
}: {
  value: string;
  form: FormInstance<CoverWizardForm>;
  city: string;
  state: string;
  stateFieldName: keyof CoverWizardForm;
  cityFieldName: keyof CoverWizardForm;
  zipFieldName: keyof CoverWizardForm;
}) {
  return (
    <div id="Zip">
      <FormItemLabel prefixCls="ant-form-item-label" label="Zip*" />
      <InputNumber
        name={zipFieldName}
        style={{ width: "100%", marginTop: "4px" }}
        placeholder="#####"
        value={value}
        onChange={(val) => {
          const valuesToSet: Partial<CoverWizardForm> = {
            [zipFieldName]: val ?? undefined,
          };
          if (!!val && val.toString().length >= 5 && (!city || !state)) {
            const response = zipCodes.lookup(val);
            if (!!response) {
              if (!city) {
                (valuesToSet[cityFieldName] as string) = response.city;
              }
              if (!state) {
                (valuesToSet[stateFieldName] as string) = response.state;
              }
            }
          }
          form.setFieldsValue(valuesToSet);
        }}
        maxLength={5}
      />
    </div>
  );
}

export type ShippingInformationPageState = {
  currentlyAutofilledValueState: [
    AutofilledValue | null,
    React.Dispatch<React.SetStateAction<AutofilledValue | null>>
  ];
  selectedRadioState: [
    AutofillRadio,
    React.Dispatch<React.SetStateAction<AutofillRadio>>
  ];
  shipToAddressesState: [
    CustomerAddressDto[] | null,
    React.Dispatch<React.SetStateAction<CustomerAddressDto[] | null>>
  ];
  selectedShipToItemState: [
    SelectOption | null,
    React.Dispatch<React.SetStateAction<SelectOption | null>>
  ];
};

export function useShippingInformationPageState(): ShippingInformationPageState {
  const [currentlyAutofilledValue, setCurrentlyAutofilledValue] =
    useState<AutofilledValue | null>(null);
  const [selectedRadio, setSelectedRadio] = useState<AutofillRadio>(
    AutofillRadio.manual
  );
  const [shipToAddresses, setShipToAddresses] = useState<
    CustomerAddressDto[] | null
  >(null);
  const [selectedShipToItem, setSelectedShipToItem] =
    useState<SelectOption | null>(null);
  return {
    currentlyAutofilledValueState: [
      currentlyAutofilledValue,
      setCurrentlyAutofilledValue,
    ],
    selectedRadioState: [selectedRadio, setSelectedRadio],
    shipToAddressesState: [shipToAddresses, setShipToAddresses],
    selectedShipToItemState: [selectedShipToItem, setSelectedShipToItem],
  };
}
